import { HomeIcon, MessageCircleHeartIcon, ShieldIcon } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { useState } from 'react';

import { userPro } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';

import { AutoDetectSettings } from '@/components/auth/AutoDetectSettings';
import { PasskeyPrompt } from '@/components/auth/PasskeyPrompt';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { LanguageSwitch } from '@/components/common/LanguageSwitch';
import { OfflineIndicator } from '@/components/common/OfflineIndicator';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { SplitFiestaLoading } from '@/components/common/SplitFiestaLoading';
import { UserMenu } from '@/components/common/UserMenu';
import { Wordmark } from '@/components/common/Wordmark';
import { Footer } from '@/components/layout/Footer';
import { GlobalMobileNav } from '@/components/layout/GlobalMobileNav';
import NotificationBell from '@/components/notifications/NotificationBell';
import { ShadowModeBanner } from '@/components/shadow/ShadowModeBanner';
import { ShadowUserPanel } from '@/components/shadow/ShadowUserPanel';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

import { useFeatureFlag } from '@/hooks/queries/useFeatureFlagsQuery';
import { useAutoPasskeyPrompt } from '@/hooks/usePasskeyPrompt';
import { useRouteChange } from '@/hooks/useRouteChangeHandler';

import { useAuthStore, useSystemRole } from '@/stores/authStore';

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const user = useAuthStore(state => state.user);
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;
  const { isChangingRoute } = useRouteChange();
  const { showPrompt, closePrompt } = useAutoPasskeyPrompt();
  const systemRole = useSystemRole();
  const isAdmin = systemRole === 'support' || systemRole === 'superadmin';
  const [showShadowPanel, setShowShadowPanel] = useState(false);
  // The viewer's own Pro only; a Pro group someone else holds does not count.
  const showProMark = useFeatureFlag('pro') && userPro(user).active;

  const navigation = [
    {
      name: t('common:navigation.groups'),
      href: '/groups',
      icon: HomeIcon,
    },
    {
      name: t('common:navigation.activity'),
      href: '/activity',
      icon: MessageCircleHeartIcon,
    },
  ];

  const isActive = (href: string) => {
    if (href === pathname) return true;
    if (href === '/groups' && pathname.startsWith('/groups')) return true;
    return false;
  };

  if (!user) {
    return null;
  }

  return (
    <div
      className="min-h-screen bg-canvas dark:bg-canvas-dark flex flex-col"
      data-testid="app-layout"
    >
      {/* Offline Status Banner */}
      <OfflineIndicator />

      {/* Shadow Mode Banner */}
      <ShadowModeBanner />

      {/* Navigation Header */}
      <nav
        className="bg-white dark:bg-gray-900 shadow-xs border-b border-gray-200 dark:border-gray-700"
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo and Desktop Navigation */}
            <div className="flex items-center">
              <div className="relative">
                <RouteChangeLink
                  href="/groups"
                  className="flex items-center"
                  data-testid="nav-logo"
                >
                  <Wordmark height={18} pro={showProMark} />
                </RouteChangeLink>
              </div>

              {/* Desktop Navigation Links */}
              <div className="hidden sm:flex sm:ml-8 sm:space-x-6 h-full">
                {navigation.map(item => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <RouteChangeLink
                      key={item.name}
                      href={item.href as `/${string}`}
                      className={`inline-flex items-center px-1 border-b-2 text-sm font-medium h-full ${
                        active
                          ? 'border-orange-500 text-gray-900 dark:text-white'
                          : 'border-transparent text-gray-500 dark:text-gray-300 hover:border-orange-300 hover:text-gray-700 dark:hover:text-gray-100'
                      }`}
                      data-testid={`nav-${item.href.slice(1)}-link`}
                    >
                      <Icon className="w-5 h-5 mr-1.5" />
                      {item.name}
                    </RouteChangeLink>
                  );
                })}
              </div>
            </div>

            {/* Desktop controls */}
            <div className="hidden sm:flex sm:items-center">
              <div className="mr-2">
                <LanguageSwitch variant="clean" />
              </div>
              <div className="h-5 w-px bg-gray-300 dark:bg-gray-600" />
              <div className="ml-3 relative">
                <NotificationBell />
              </div>
              <div className="ml-3 h-5 w-px bg-gray-300 dark:bg-gray-600" />
              <div className="ml-3">
                <ThemeToggle data-testid="nav-theme-toggle" />
              </div>
              {isAdmin && (
                <>
                  <div className="ml-3 h-5 w-px bg-gray-300 dark:bg-gray-600" />
                  <button
                    onClick={() => setShowShadowPanel(true)}
                    className="ml-3 p-1.5 text-gray-500 hover:text-orange-500 dark:text-gray-400 dark:hover:text-orange-400 transition-colors"
                    title="Shadow User"
                    data-testid="nav-shadow-btn"
                  >
                    <ShieldIcon className="w-5 h-5" />
                  </button>
                </>
              )}
              <div className="ml-3 h-5 w-px bg-gray-300 dark:bg-gray-600" />
              <div className="ml-3">
                <UserMenu data-testid="nav-user-menu" />
              </div>
            </div>

            {/* Mobile controls - simplified */}
            <div className="sm:hidden flex items-center space-x-2">
              <LanguageSwitch variant="clean" />
              <ThemeToggle data-testid="mobile-theme-toggle" />
              {isAdmin && (
                <button
                  onClick={() => setShowShadowPanel(true)}
                  className="p-1.5 text-gray-500 hover:text-orange-500 dark:text-gray-400 dark:hover:text-orange-400 transition-colors"
                  title="Shadow User"
                  data-testid="mobile-shadow-btn"
                >
                  <ShieldIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main
        className="flex-1 max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 w-full pb-36 sm:pb-6"
        data-testid="main-content"
      >
        {isChangingRoute ? (
          <div className="min-h-[70vh] flex items-center justify-center">
            <SplitFiestaLoading className="bg-transparent" />
          </div>
        ) : (
          <ErrorBoundary>{children}</ErrorBoundary>
        )}
      </main>

      {/* Footer - hide on mobile since we have bottom nav */}
      <div className="hidden sm:block">
        <Footer />
      </div>

      {/* Global Mobile Navigation */}
      <GlobalMobileNav />

      {/* Passkey Registration Prompt */}
      <PasskeyPrompt isOpen={showPrompt} onClose={closePrompt} />

      {/* Auto-detect and save browser locale preferences */}
      <AutoDetectSettings />

      {/* Shadow User Panel (admin only) */}
      {isAdmin && (
        <ShadowUserPanel
          isOpen={showShadowPanel}
          onClose={() => setShowShadowPanel(false)}
        />
      )}
    </div>
  );
}
